import type { CalendarEvent } from '../../../types';
import type { BorderStyle, EventVariant } from '../types';

/**
 * Determine visual event variant from event.resource properties
 * Returns ONLY styling-related decisions (variant type and border style)
 *
 * Uses real API fields: status (AppointmentStatus) and confirmation_status
 *
 * @param event - Calendar event to analyze
 * @returns Visual variant configuration (4 possible combinations)
 *
 * Four variant combinations:
 * 1. actionable + dashed  - Suggested appointments (status === "suggested")
 * 2. actionable + solid   - Patient-initiated actions (cancelled/reschedule_requested)
 * 3. block                - Calendar events/blocks (TBD)
 * 4. standard             - Normal appointments
 */
export const getEventVariant = (
  event: CalendarEvent,
): {
  variant: EventVariant;
  borderStyle?: BorderStyle;
} => {
  const resource = event.resource;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (resource.encounter?.status === 'completed') {
    return {
      variant: 'standard',
    };
  }

  // Suggested appointments (provider/system suggested) - dashed border
  if (resource.status === 'suggested') {
    return {
      variant: 'actionable',
      borderStyle: 'dashed',
    };
  }

  // Patient-initiated actions (cancel or reschedule request) - solid border
  if (
    resource.confirmation_status === 'cancelled' ||
    resource.confirmation_status === 'reschedule_requested'
  ) {
    return {
      variant: 'actionable',
      borderStyle: 'solid',
    };
  }

  // Calendar events/blocks (non-appointment time blocks)
  // @ts-expect-error - Future-proofing for is_calendar_event property (TBD)
  if (resource.is_calendar_event === true) {
    return {
      variant: 'block',
    };
  }

  // Default: Standard appointment
  return {
    variant: 'standard',
  };
};
